/**
 * ショップ：おはしと買い切り。（2026-10-04 「おみせ」から改名。飲食店の「お店」と紛らわしかった）
 *
 * **Web版では開かない**（assist.enabled が false）。アプリ版と、staging のテスト用モックだけ。
 * メニューは増やさない：おはし2種＋買い切り1本＋広告で1本。
 */

import { track } from '../meta/telemetry';
import type { AssistController } from '../meta/assist';
import type { PurchaseOutcome } from '../platform/Platform';
import { PRODUCTS, type ProductId } from '../platform/products';

/** 行の下に出すひとこと。good＝うまくいった／bad＝できなかった／info＝お待ちください。 */
export interface RowNotice {
  text: string;
  tone: 'good' | 'bad' | 'info';
}

/** ショップを開いた場所（計測の shop_open の detail）。 */
export type ShopSource = 'settings' | 'result' | 'ohashi_button' | 'other';

/** 購入の結果を、その行に出すひとことにする。やめたときは何も出さない。 */
export function purchaseNotice(outcome: PurchaseOutcome, title: string): RowNotice | null {
  switch (outcome) {
    case 'purchased':
      return { text: `${title}を購入しました。`, tone: 'good' };
    case 'cancelled':
      return null;
    case 'pending':
      return { text: '購入の承認を待っています。承認されると、次にひらいたときに届きます。', tone: 'good' };
    case 'failed':
    default:
      return { text: '購入できませんでした。時間をおいて、もう一度お試しください。', tone: 'bad' };
  }
}

/** 復元の結果のひとこと。 */
export function restoreNotice(result: 'restored' | 'none' | 'failed'): RowNotice {
  if (result === 'restored') return { text: '買い切りを復元しました。', tone: 'good' };
  if (result === 'none') return { text: '復元できる購入は見つかりませんでした。', tone: 'good' };
  return { text: '復元できませんでした。時間をおいて、もう一度お試しください。', tone: 'bad' };
}

/**
 * ストアから値段が取れていないときは買えなくする（押しても何も起きない、を防ぐ）。
 * 値段はショップを開くたびに取り直す（loading＝取り直している最中）。
 */
export function productButtonState(
  price: string | null,
  loading = false,
): { label: string; disabled: boolean; notice: RowNotice | null } {
  if (price) return { label: price, disabled: false, notice: null };
  if (loading) {
    return {
      label: '…',
      disabled: true,
      notice: { text: 'ストアから値段を読み込んでいます。', tone: 'info' },
    };
  }
  return {
    label: '—',
    disabled: true,
    notice: {
      text: 'ストアにつながりませんでした。通信のよいところで、ショップをもう一度ひらいてください。',
      tone: 'bad',
    },
  };
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function noticeNode(notice: RowNotice): HTMLElement {
  const node = el('p', `shop-row__notice shop-row__notice--${notice.tone}`, notice.text);
  // 読み上げにも結果を知らせる
  node.setAttribute('role', notice.tone === 'bad' ? 'alert' : 'status');
  return node;
}

export class ShopModal {
  private readonly root: HTMLElement;
  private readonly body: HTMLElement;
  private readonly closeBtn: HTMLButtonElement;
  private message = '';
  /** 行ごとのひとこと（購入・復元の結果）。開き直すと消える。 */
  private readonly rowNotices = new Map<string, RowNotice>();
  /** ストアに値段を問い合わせ直している最中。 */
  private priceLoading = false;

  onClose?: () => void;

  constructor(private readonly assist: AssistController) {
    this.root = el('div', 'modal shop-modal');
    this.root.dataset.visible = 'false';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-labelledby', 'shop-title');
    const backdrop = el('div', 'modal__backdrop');
    backdrop.addEventListener('click', () => this.hide());
    const card = el('div', 'modal__card');
    const head = el('div', 'modal__head');
    const title = el('h2', 'modal__title', 'ショップ');
    title.id = 'shop-title';
    this.closeBtn = el('button', 'btn btn--icon');
    this.closeBtn.type = 'button';
    this.closeBtn.setAttribute('aria-label', 'ショップを閉じる');
    this.closeBtn.append(el('span', undefined, '✕'));
    this.closeBtn.addEventListener('click', () => this.hide());
    head.append(title, this.closeBtn);
    this.body = el('div', 'modal__body shop-body');
    card.append(head, this.body);
    this.root.append(backdrop, card);
    document.body.append(this.root);

    assist.onChange(() => {
      if (this.isOpen()) this.render();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen()) this.hide();
    });
  }

  /** @param source 開いた場所（計測用） */
  open(source: ShopSource = 'other'): void {
    if (!this.assist.enabled) return;
    track('shop_open', { detail: source });
    if (this.assist.canEarnItemByAd()) track('ad_offer', { detail: 'ohashi' });
    this.message = '';
    this.rowNotices.clear();
    this.refreshPricesIfMissing();
    this.render();
    this.root.dataset.visible = 'true';
    this.closeBtn.focus();
  }

  /** 起動時に値段が取れていなければ、開いたときに取り直す（取れたら行を押せるようにする）。 */
  private refreshPricesIfMissing(): void {
    const a = this.assist;
    if (!a.canPurchase || this.priceLoading) return;
    const ids = Object.keys(PRODUCTS) as ProductId[];
    if (ids.every((id) => a.platform.priceOf(id))) return;
    this.priceLoading = true;
    void a.platform
      .refreshPrices()
      .catch(() => false)
      .then((ok) => {
        track('price_missing', { detail: ok ? 'ok' : 'ng' });
        this.priceLoading = false;
        if (this.isOpen()) this.render();
      });
  }

  hide(): void {
    if (!this.isOpen()) return;
    this.root.dataset.visible = 'false';
    this.onClose?.();
  }

  isOpen(): boolean {
    return this.root.dataset.visible === 'true';
  }

  private render(): void {
    const a = this.assist;
    const nodes: HTMLElement[] = [];

    const stock = el('p', 'shop-stock');
    stock.append('いま持っている おはし ', el('b', undefined, `${a.items} 本`));
    nodes.push(stock);
    nodes.push(
      el(
        'p',
        'shop-note',
        'おはしは、器の中の料理を1つつまんで消せます。Lv11どうしのあいだに挟まった料理をどけたいときに。おはしやコンティニューで届いたLv12も金メダルです（白金メダルは使わずに届いたときだけ）。',
      ),
    );

    // 買い切りは、まだ持っていない人には先頭に出す（毎日のおはし・限定の器を先に見せる。2026-10-11）
    const buyoutFirst = a.canPurchase && !a.adFree;
    if (buyoutFirst) nodes.push(this.buyoutSection());

    const items = el('section', 'shop-section');
    items.append(el('h3', 'shop-section__title', 'おはし'));
    items.append(
      this.row(
        'ad_item',
        '広告を見て1本もらう',
        a.canEarnItemByAd()
          ? '1プレイに1回・1日3本まで'
          : a.adItemLimitReason() === 'day'
            ? '今日の分は受け取りました（あしたまた）'
            : 'このプレイではもう受け取りました',
        '見る',
        !a.canEarnItemByAd(),
        async () => {
          const ok = await a.earnItemByAd();
          this.message = ok ? 'おはしを1本受け取りました。' : '広告を最後まで見ると受け取れます。';
        },
      ),
    );
    if (a.canPurchase) items.append(this.productRow('items_5'), this.productRow('items_20'));
    nodes.push(items);

    // Android 版は広告だけ（課金なし）。買い切りの段ごと出さない
    if (a.canPurchase && !buyoutFirst) nodes.push(this.buyoutSection());

    if (this.message) nodes.push(el('p', 'shop-message', this.message));
    if (a.platform.kind === 'mock') {
      nodes.push(el('p', 'shop-mock', 'テスト用の画面です。お金は動きません。'));
    }
    this.body.replaceChildren(...nodes);
  }

  /**
   * 押したボタンの行へフォーカスを戻す。描き直すとボタンが作り直され、購入に失敗したあと
   * フォーカスが body に落ちていた（2026-10-05）。押せなくなっていれば閉じるボタンへ。
   */
  private refocus(key: string): void {
    if (!this.isOpen()) return;
    const target = this.body.querySelector<HTMLButtonElement>(`[data-shop-key="${key}"]`);
    if (target && !target.disabled) target.focus({ preventScroll: false });
    else this.closeBtn.focus({ preventScroll: true });
  }

  private buyoutSection(): HTMLElement {
    const a = this.assist;
    const buyout = el('section', 'shop-section');
    buyout.append(el('h3', 'shop-section__title', '買い切り'));
    if (a.adFree) {
      buyout.append(el('p', 'shop-owned', '購入済みです。ゲームの合間の広告は出ません。毎日おはしが1本届きます。'));
    } else {
      // 合間の広告が減るだけでなく、毎日のおはしと限定の器があることを先に見せる
      const perks = el('ul', 'shop-perks');
      [
        '毎日おはしが1本届く（ずっと）',
        '買い切りだけの器「朱の漆椀」',
        'ゲームの合間の広告が出ない',
      ].forEach((text) => perks.append(el('li', undefined, text)));
      buyout.append(perks, this.productRow('ad_free'));
    }
    const restore = el('button', 'btn shop-restore', '購入を復元する');
    restore.type = 'button';
    restore.dataset.shopKey = 'restore';
    restore.addEventListener('click', async () => {
      restore.disabled = true;
      const result = await a.restore().catch(() => 'failed' as const);
      this.rowNotices.set('restore', restoreNotice(result));
      this.render();
      this.refocus('restore');
    });
    buyout.append(restore);
    const notice = this.rowNotices.get('restore');
    if (notice) buyout.append(noticeNode(notice));
    return buyout;
  }

  private productRow(id: ProductId): HTMLElement {
    const product = PRODUCTS[id];
    const state = productButtonState(this.assist.platform.priceOf(id), this.priceLoading);
    const row = this.row(id, product.title, product.description, state.label, state.disabled, async () => {
      const outcome = await this.assist.buy(id);
      const notice = purchaseNotice(outcome, product.title);
      if (notice) this.rowNotices.set(id, notice);
      else this.rowNotices.delete(id);
    });
    const notice = state.notice ?? this.rowNotices.get(id);
    if (notice) row.append(noticeNode(notice));
    return row;
  }

  private row(
    key: string,
    title: string,
    note: string,
    action: string,
    disabled: boolean,
    onClick: () => Promise<void>,
  ): HTMLElement {
    const row = el('div', 'shop-row');
    const text = el('div', 'shop-row__text');
    text.append(el('span', 'shop-row__title', title), el('span', 'shop-row__note', note));
    const btn = el('button', 'btn btn--primary shop-row__btn', action);
    btn.type = 'button';
    btn.dataset.shopKey = key;
    btn.disabled = disabled;
    btn.addEventListener('click', async () => {
      btn.disabled = true;
      try {
        await onClick();
      } finally {
        this.render();
        this.refocus(key);
      }
    });
    row.append(text, btn);
    return row;
  }
}
